import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import TextInput from "components/TextInput";
import SelectInput from "components/SelectInput";
import DateInput from "components/DateInput";
import CheckboxInput from "components/CheckboxInput";
import CountryCodeSelector from "components/CountryCodeSelector";
import ButtonContained from "components/ButtonContained";
import { validateEmail, getDateFormat } from "utilities";
import { toastManager } from "components/ToastNotification";
import { fetchRoleList } from "actions/Roles";
import { createUser } from "actions/Users";
import { fetchFeatureFlagAssignments, fetchAllSchedules } from "actions/Settings";
import { TenantIcon, EmployeeIcon, VendorIcon } from "utilities/iconsNew";

const AddUserInfo = ({ formData, updateForm, onNext, onBack, facility, onClose, preSelectedUnit }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.authUser);
  const site = useSelector((state) => state.auth.site);
  const roles = useSelector((state) => state.roles.rolesList);
  const schedules = useSelector((state) => state.settings.siteSchedules);
  const featureFlags = useSelector((state) => state.settings.featureFlagsAssignmentsList.filter(f => f.type === "all users").map(ff => ff.featureflag.name));

  useEffect(() => {
    dispatch(fetchRoleList(undefined, {
      facilityUUID: facility.uuid,
      companyUUID: facility.companyUUID
    }));
    if (formData.userType === "employee") {
      dispatch(fetchFeatureFlagAssignments({
        facilityUUID: facility.uuid,
        companyUUID: facility.companyUUID
      }));
      dispatch(fetchAllSchedules({
        facilityUUID: facility.uuid,
        companyUUID: facility.companyUUID
      }));
    }
  }, [dispatch]);

  const types = [
    {
      name: "tenant",
      title: "Tenant",
      icon: <TenantIcon />,
      text: intl.formatMessage({ id: "pages.users.create.tenant.desc" }),
    },
    {
      name: "employee",
      title: "Employee",
      icon: <EmployeeIcon />,
      text: intl.formatMessage({ id: "pages.users.create.employee.desc" }),
    },
    {
      name: "limited",
      title: "Service/Vendor",
      icon: <VendorIcon />,
      text: intl.formatMessage({ id: "comp.simple.userCreate" }),
    },
  ];
  const type = types.find((type) => type.name === formData.userType);
  const checkRoles = (role) => {
    // Tier 1 roles are always hidden
    if (role.tier === 1) return false;
  
    const hasAdmin = authUser.permissions.includes("manage_users_admin");
    const hasManage = authUser.permissions.includes("manage_users");
    const userTier = authUser.role.tier;
  
    switch (formData.userType) {
      case "tenant":
        return role.tier === 0;
  
      case "employee":
        if (hasAdmin) return role.tier >= userTier;
        if (hasManage) return role.tier > userTier;
        break;
  
      case "limited":
        return role.tier > userTier || role.tier === 0;
  
      default:
        return false;
    }
  
    return false;
  };

  const isEmployee = formData.userType === "employee";
  const hasFeatureFlags = featureFlags || [];
  const missingScheduleFlags = !(
    hasFeatureFlags.includes("threeScheduleEnabled") &&
    hasFeatureFlags.includes("newScheduleEnabled")
  );
  const noSchedules = schedules?.length === 0;
  const oldSchedule = isEmployee && (missingScheduleFlags || noSchedules);

  const canProceed = () => {
    const { firstName, lastName, email, phone, userType, role, accessCode, expirationDate } = formData;
    if (firstName === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.firstName"}));
      return;
    } else if (lastName === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.lastName"}));
      return;
    } else if (email !== "" && !validateEmail(email)) {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.fail.email"}));
      return;
    } else if (phone !== "" && phone.length < 4) {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.phoneEmail"}));
      return;
    } else if (role === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.UserRoleId"}));
      return;
    } else if ((facility.has3K || facility.has4K || facility.hasKeypad > 0) && accessCode === "" && userType !== "employee") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.accessCode"}));
      return;
    } else if (accessCode !== "" && accessCode.length < 4) {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.fail.accessCode.short"}));
      return;
    } else if (accessCode !== "" && accessCode.length > 12) {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.fail.accessCode.long"}));
      return;
    } else {
      if (oldSchedule) {
        dispatch(createUser(
          {
            accessCode: formData.accessCode,
            companyUUID: facility.companyUUID,
            countryCode: formData.country.phone[0].toString(),
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            loginType: "noke",
            phone: formData.phone,
            role: formData.role.uuid,
            roleAtAllSites: formData.assignToAllSites,
          },
          undefined,
          {
            facilityUUID: facility.uuid,
            companyUUID: facility.companyUUID,
            messageOne: intl.formatMessage({id: "notifications.success.create.workDone"}),
            messageTwo: intl.formatMessage({id: "notifications.success.edit"})
          }
        ));
        onClose();
      } else {
        onNext();
      }
    }
  }
  const roleOptions = roles && roles.filter(role => checkRoles(role));
  const roleMap = roleOptions && Object.fromEntries(roleOptions.map(role => [role.uuid, role]));
  return (
    <div className="px-18 flex flex-col">
      <div className="flex gap-4">
        <div className="p-5 flex-1 flex flex-col items-center justify-center">
          {type.icon}
          <h5 className="pt-6 pb-2 tw-heading-5 text-[var(--color-blue-600)]">{type.title}</h5>
          <p className="tw-paragraph-reg text-[var(--color-gray-400)]">{type.text}</p>
        </div>
        <div className="flex-1 flex flex-col gap-4">
          <TextInput
            id="add-user-info-first-name"
            dataCy="add-user-info-first-name"
            placeholder={`${intl.formatMessage({id: "form.firstName"})} (${intl.formatMessage({id: "form.required"})})`}
            onChange={(e) => updateForm("firstName")(e.target.value)}
            value={formData.firstName}
          />
          <TextInput
            id="add-user-info-last-name"
            dataCy="add-user-info-last-name"
            placeholder={`${intl.formatMessage({id: "form.lastName"})} (${intl.formatMessage({id: "form.required"})})`}
            onChange={(e) => updateForm("lastName")(e.target.value)}
            value={formData.lastName}
          />
          <TextInput
            id="add-user-info-email"
            dataCy="add-user-info-email"
            placeholder={`${intl.formatMessage({id: "form.email"})}`}
            onChange={(e) => updateForm("email")(e.target.value)}
            isDisabled={formData.userType === "tenant" && site?.mobileOnlyTenantAccounts === "true"}
            value={formData.email}
          />
          <div className="flex items-center gap-2 h-[53px]">
            <CountryCodeSelector
              selectedCountry={formData.country}
              onSelect={(country) => updateForm("country")(country)}
            />
            <TextInput
              id="add-user-info-phone"
              dataCy="add-user-info-phone"
              placeholder={`${intl.formatMessage({id: "form.phone"})}`}
              onChange={(e) => updateForm("phone")(e.target.value)}
              value={formData.phone}
              validateInput={(val) => /^\d*$/.test(val)} // only digits
            />
          </div>
          {formData.userType === "limited" && <DateInput
            id="add-user-info-expiration-date"
            dataCy="add-user-info-expiration-date"
            placeholder={intl.formatMessage({id: "comp.repeatSchedule.expirationDate"})}
            date={formData.expirationDate}
            onDateChange={(date) => updateForm("expirationDate")(date)}
            displayFormat={facility.sitePreferredDateFormat ? facility.sitePreferredDateFormat : "MM/DD/YYYY"}
          />}
          <SelectInput
            id="add-user-info-role"
            dataCy="add-user-info-role"
            value={formData.role?.uuid || ""}
            placeholder={`${intl.formatMessage({id: "form.role"})} (${intl.formatMessage({id: "form.required"})})`}
            onChange={(e) => {
              const selectedUuid = e.target.value;
              const selectedRole = roleMap[selectedUuid];
              updateForm("role")(selectedRole)
            }}
            options={
              roleOptions
              ? roleOptions.map(role => ({
                value: role.uuid,
                name: role.name
              }))
              : []
            }
          />
          {(facility.has3K || facility.has4K || facility.hasKeypad > 0) && <TextInput
            id="add-user-info-access-code"
            dataCy="add-user-info-access-code"
            placeholder={
              formData.userType === "employee"
              ? intl.formatMessage({id: "form.accessCode"})
              : `${intl.formatMessage({id: "form.accessCode"})} (${intl.formatMessage({id: "form.required"})})`}
            onChange={(e) => updateForm("accessCode")(e.target.value)}
            value={formData.accessCode}
            validateInput={(val) => /^\d*$/.test(val)} // only digits
          />}
          {formData.userType === "employee" &&
            authUser &&
            (authUser.role.tier === 1 || authUser.roleAtAllSites) && <CheckboxInput
            id="add-user-info-assign-to-all-sites"
            dataCy="add-user-info-assign-to-all-sites"
            label={intl.formatMessage({id: "pages.users.create.assignToAll"})}
            checked={formData.assignToAllSites}
            onChange={(val) => updateForm("assignToAllSites")(val)}
          />}
        </div>
      </div>
      <div className="flex justify-between pt-6">
        {/* If we have already preselected a unit that means we don't want to go back to selecting a type...we are coming here straight from the unit assign modal */}
        {preSelectedUnit ?
          <div></div> :
          <ButtonContained
            id="add-user-info-back-button"
            dataCy="add-user-info-back-button"
            text={intl.formatMessage({ id: `comp.button.back` })}
            type="mediumOutline"
            onClick={onBack}
          />
        }
        <ButtonContained
          id="add-user-info-next-button"
          dataCy="add-user-info-next-button"
          text={intl.formatMessage({id: oldSchedule ? "comp.button.create" : "comp.pagination.next"})}
          type="mediumOutline"
          onClick={canProceed}
        />
      </div>
    </div>
  )
}

export default AddUserInfo;